import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';

function getFixHint(error: string): string | null {
  const lower = error.toLowerCase();
  if (lower.includes('title') && lower.includes('required'))
    return 'Add a title at the top of this dialog.';
  if (lower.includes('subtask') || (lower.includes('must have') && lower.includes('block')))
    return 'Drag a content block from the right panel into the task to add at least one subtask.';
  if (
    lower.includes('timing') ||
    lower.includes('start and deadline') ||
    lower.includes('send date') ||
    lower.includes('trigger date')
  )
    return 'Open "Timing & Triggers" on the left and review the start settings.';
  if (lower.includes('subject'))
    return 'Fill in the subject line at the top of the message editor.';
  if (lower.includes('action') && lower.includes('at least'))
    return 'Click "+ Add action" to add at least one automation action.';
  if (lower.includes('url') || lower.includes('link'))
    return 'Open the subtask and paste a valid URL in the link field.';
  if (lower.includes('review your blocks') || lower.includes('review your'))
    return 'Check each subtask below — one or more have missing required fields.';
  return null;
}

// Compose the issue text shown in the action card. Prefer the hint when
// available (more actionable); fall back to the raw error otherwise.
const composeIssueText = (error: string): string => {
  const hint = getFixHint(error);
  return hint ?? error;
};

const GradientStrip = styled('div')`
  height: 3px;
  background: ${(p) => p.theme.dado.copilot.gradientHorizontal};
`;

const Banner = styled('div')`
  background: ${(p) => p.theme.dado.copilot.blueBanner};
  border-bottom: 1px solid ${(p) => p.theme.dado.copilot.blueBannerBorder};
  padding: 12px 20px;
`;

const HeaderRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const HeaderLeft = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CopilotMark = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.dado.copilot.purpleAccent};
`;

const Divider = styled('div')`
  width: 1px;
  height: 12px;
  background: ${(p) => p.theme.dado.copilot.blueBannerBorder};
`;

const CountLabel = styled('div')`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
  color: #0052cc;
`;

const HeaderRight = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PageLabel = styled('span')`
  font-size: 12px;
  font-weight: 300;
  color: #778088;
`;

const NextButton = styled('button')`
  height: 28px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: #0e78e4;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  transition: filter 120ms ease;

  &:hover {
    filter: brightness(1.05);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(14, 120, 228, 0.25);
  }
`;

const CloseButton = styled('button')`
  border: none;
  background: transparent;
  color: #9ba4ac;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  transition: color 120ms ease;

  &:hover {
    color: #5f676e;
  }
  &:focus-visible {
    outline: 2px solid #0e78e4;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const ActionCard = styled('div')`
  background: #fff;
  border-radius: 6px;
  padding: 10px 14px;
  box-shadow:
    0 1px 3px rgba(8, 18, 26, 0.06),
    inset 0 0 0 1px ${(p) => p.theme.dado.copilot.blueBannerBorder};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const IssueText = styled('span')`
  font-size: 13px;
  font-weight: 300;
  color: #5f676e;
  line-height: 1.55;
`;

const CompleteButton = styled('button')`
  height: 28px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: #0e78e4;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  flex-shrink: 0;
  transition: filter 120ms ease;

  &:hover {
    filter: brightness(1.05);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(14, 120, 228, 0.25);
  }
`;

const Sparkle: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
    <path d="M6 0L7.2 4.8L12 6L7.2 7.2L6 12L4.8 7.2L0 6L4.8 4.8L6 0Z" />
  </svg>
);

const InfoIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="5" stroke="#0E78E4" strokeWidth="1.5" />
    <path
      d="M7 5V5.5M7 7.5V10"
      stroke="#0E78E4"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ArrowRight: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M2 7H12M8 3L12 7L8 11"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M2 2L12 12M12 2L2 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

interface ValidationBannerProps {
  errors: string[];
}

export const ValidationBanner: React.FC<React.PropsWithChildren<ValidationBannerProps>> = ({
  errors,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  // Local dismiss state — re-validation (new errors reference) resets it
  // so the banner can reappear when fresh issues land.
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
    setCurrentIndex(0);
  }, [errors]);

  if (!errors.length || dismissed) return null;

  const total = errors.length;
  const isMulti = total > 1;
  const safeIndex = Math.min(currentIndex, total - 1);
  const current = errors[safeIndex];

  const advance = () => {
    if (safeIndex + 1 >= total) {
      setDismissed(true);
    } else {
      setCurrentIndex(safeIndex + 1);
    }
  };

  return (
    <>
      <GradientStrip />
      <Banner role="alert" aria-live="polite">
        <HeaderRow>
          <HeaderLeft>
            <CopilotMark>
              <Sparkle />
              Copilot
            </CopilotMark>
            <Divider />
            <CountLabel>
              <InfoIcon />
              {total === 1 ? '1 item to complete' : `${total} items to complete`}
            </CountLabel>
          </HeaderLeft>
          <HeaderRight>
            {isMulti && (
              <>
                <PageLabel>
                  {safeIndex + 1} of {total}
                </PageLabel>
                <NextButton type="button" onClick={advance}>
                  Next item
                  <ArrowRight />
                </NextButton>
              </>
            )}
            <CloseButton
              type="button"
              onClick={() => setDismissed(true)}
              aria-label="Dismiss validation banner"
            >
              <CloseIcon />
            </CloseButton>
          </HeaderRight>
        </HeaderRow>
        <ActionCard>
          <IssueText>{composeIssueText(current)}</IssueText>
          <CompleteButton
            type="button"
            onClick={advance}
            aria-label={
              isMulti && safeIndex + 1 < total
                ? 'Mark complete and go to next item'
                : 'Mark complete and dismiss'
            }
          >
            Complete
            <ArrowRight />
          </CompleteButton>
        </ActionCard>
      </Banner>
    </>
  );
};
